import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getAdminSession, isSuperAdmin, canView, firstAccessiblePath, clearAdminSession } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { ShieldOff, LogOut } from 'lucide-react';

const NoAccessScreen = () => {
    const navigate = useNavigate();
    return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4" data-testid="admin-no-access-screen">
            <div className="max-w-md text-center">
                <span className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent">
                    <ShieldOff className="h-7 w-7 text-muted-foreground" />
                </span>
                <h1 className="font-display text-xl font-semibold">No sections assigned</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                    Your account doesn't have access to any part of the admin panel yet.
                    Ask a super admin to grant you permissions.
                </p>
                <Button
                    variant="outline"
                    className="mt-6"
                    data-testid="admin-no-access-logout"
                    onClick={() => { clearAdminSession(); navigate('/admin/login'); }}
                >
                    <LogOut className="mr-2 h-4 w-4" /> Log out
                </Button>
            </div>
        </div>
    );
};

const RequireAdmin = ({ children, superOnly = false, section = null }) => {
    const location = useLocation();
    const session = getAdminSession();
    if (!session) {
        return <Navigate to="/admin/login" state={{ from: location }} replace />;
    }
    if (superOnly && !isSuperAdmin()) {
        const fallback = firstAccessiblePath();
        return fallback ? <Navigate to={fallback} replace /> : <NoAccessScreen />;
    }
    if (section && !canView(section)) {
        const fallback = firstAccessiblePath();
        if (fallback && fallback !== location.pathname) {
            return <Navigate to={fallback} replace />;
        }
        return <NoAccessScreen />;
    }
    return children;
};

export default RequireAdmin;
